import maplibregl from 'maplibre-gl';
import { Chart } from 'chart.js/auto';
import store from '../../../../state';
import { getApiServer, appendAuthToken } from '../../../../config';
import authService from '../../../../services/AuthService';
import { fetchLightningTrend } from '../../../../services/PoiDataService';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService, { getRequiredTierForLayer } from '../../../../services/TierAccessService';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';

export class LightningMapWidget implements WidgetRenderer {
  private getDayCardLabel(baseDateStr: string, offsetDays: number): string {
    const parts = (baseDateStr || '').split('-').map(Number);
    if (parts.length === 3 && !parts.some(isNaN)) {
      const utcNoon = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2] + offsetDays, 12, 0, 0));
      const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
      const shortDate = `${dayNames[utcNoon.getUTCDay()]} ${utcNoon.getUTCMonth() + 1}/${utcNoon.getUTCDate()}`;
      return offsetDays === 0 ? `Today · ${shortDate} (Peak)` : `${shortDate} (24h Peak)`;
    }
    return offsetDays === 0 ? 'Today (Peak)' : `Day ${offsetDays + 1} (24h Peak)`;
  }

  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const isSingleDay = String(ctx.viewDays).toLowerCase() === '1';
    const title = widget.title || `${!isSingleDay ? 'Multi-Day ' : ''}Daily Lightning Hazard & Strike Timing`;

    let lightningMapsHtml = '';
    if (isSingleDay) {
      lightningMapsHtml = `
        <div style="display: flex; gap: 16px; align-items: center; width: 100%; flex-wrap: wrap;">
          <div id="poi-lightning-map-0" style="height: 280px; width: 280px; max-width: 100%; border-radius: 6px; background: #0f172a;"></div>
          <div style="flex: 1; min-width: 240px; display: flex; flex-direction: column; gap: 6px;">
            <div style="font-size: 0.75rem; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.5px;">Diurnal Strike Likelihood (Next 24 Hours)</div>
            <div style="height: 140px; position: relative;">
              <canvas id="poi-lightning-trend-chart-0"></canvas>
            </div>
          </div>
        </div>
      `;
    } else {
      const label0 = this.getDayCardLabel(ctx.dateStr, 0);
      const label1 = this.getDayCardLabel(ctx.dateStr, 1);
      const label2 = this.getDayCardLabel(ctx.dateStr, 2);
      lightningMapsHtml = `
        <div style="display: flex; gap: 14px; flex-wrap: wrap; justify-content: center; width: 100%;">
          <div style="display: flex; flex-direction: column; align-items: center; gap: 6px; width: 220px;">
            <div style="font-size: 0.72rem; color: #94a3b8; font-weight: bold;">${label0}</div>
            <div id="poi-lightning-map-0" style="height: 180px; width: 220px; border-radius: 6px; background: #0f172a; position: relative;"></div>
            <div style="width: 100%; height: 90px; position: relative;">
              <canvas id="poi-lightning-trend-chart-0"></canvas>
            </div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: center; gap: 6px; width: 220px;">
            <div style="font-size: 0.72rem; color: #94a3b8; font-weight: bold;">${label1}</div>
            <div id="poi-lightning-map-1" style="height: 180px; width: 220px; border-radius: 6px; background: #0f172a; position: relative;"></div>
            <div style="width: 100%; height: 90px; position: relative;">
              <canvas id="poi-lightning-trend-chart-1"></canvas>
            </div>
          </div>
          <div style="display: flex; flex-direction: column; align-items: center; gap: 6px; width: 220px;">
            <div style="font-size: 0.72rem; color: #94a3b8; font-weight: bold;">${label2}</div>
            <div id="poi-lightning-map-2" style="height: 180px; width: 220px; border-radius: 6px; background: #0f172a; position: relative;"></div>
            <div style="width: 100%; height: 90px; position: relative;">
              <canvas id="poi-lightning-trend-chart-2"></canvas>
            </div>
          </div>
        </div>
      `;
    }

    return `
      <div class="metric-card" style="background: rgba(255,255,255,0.03); border: 1px solid var(--border-light); border-radius: 8px; padding: 12px; display: flex; flex-direction: column; align-items: center;">
        <h3 style="margin: 0 0 12px 0; color: #f8fafc; font-size: 0.95rem; align-self: flex-start; width: 100%;">⚡ ${title}</h3>
        ${lightningMapsHtml}
      </div>
    `;
  }

  public init(_widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const lat = ctx.lat;
    const lon = ctx.lon;
    const state = store.getState();
    const manifest = ctx.manifest;
    const apiServer = getApiServer(manifest);
    const tileRoot = (manifest ? manifest.tile_base : 'tiles').replace(/^\/+|\/+$/g, '');
    const cleanServer = apiServer.replace(/\/$/, '');
    const cleanViewStr = String(ctx.viewDays).toLowerCase();
    const mapCount = cleanViewStr === '1' ? 1 : 3;

    for (let i = 0; i < mapCount; i++) {
      const containerId = `poi-lightning-map-${i}`;
      const container = document.getElementById(containerId);
      if (!container) continue;

      const dateParts = (ctx.dateStr || '').split('-').map(Number);
      const curDateStr = (dateParts.length === 3 && !dateParts.some(isNaN))
        ? new Date(Date.UTC(dateParts[0], dateParts[1] - 1, dateParts[2] + i, 12, 0, 0)).toISOString().split('T')[0]
        : ctx.dateStr;

      const mapInstance = new maplibregl.Map({
        container: containerId,
        style: {
          version: 8,
          sources: {
            osm: {
              type: 'raster',
              tiles: ['https://a.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png'],
              tileSize: 256
            }
          },
          layers: [
            {
              id: 'osm-layer',
              type: 'raster',
              source: 'osm',
              minzoom: 0,
              maxzoom: 18
            }
          ]
        },
        center: [lon, lat],
        zoom: 5,
        interactive: false,
        attributionControl: false
      });

      new maplibregl.Marker({ color: '#facc15' })
        .setLngLat([lon, lat])
        .addTo(mapInstance);

      ctx.maps.push(mapInstance);

      let cleanPath = `${tileRoot}/forecast/lightning/lightning_forecast/lightning_24h_${curDateStr}.json`.replace(/\/+/g, '/');
      if (cleanPath.startsWith('/')) {
        cleanPath = cleanPath.substring(1);
      }
      const url = appendAuthToken(`${cleanServer}/${cleanPath}`);
      const token = authService.getSessionToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      mapInstance.on('load', async () => {
        if (!TierAccessService.userHasLayerAccess('lightning')) return;
        try {
          const res = await fetch(url, {
            headers,
            credentials: url.includes('auth.skyskout.com') ? 'include' : 'same-origin'
          });
          if (!res.ok) return;
          const geojson = await res.json();

          if (mapInstance && mapInstance.getStyle()) {
            mapInstance.addSource('lightning-source', {
              type: 'geojson',
              data: geojson
            });

            mapInstance.addLayer({
              id: 'lightning-layer',
              type: 'fill',
              source: 'lightning-source',
              paint: {
                'fill-color': [
                  'match',
                  ['get', 'level'],
                  1, '#9664ff',
                  2, '#ff8000',
                  3, '#ff0000',
                  'transparent'
                ],
                'fill-opacity': 0.6 * state.weatherOpacity
              }
            });
          }
        } catch (err) {
          console.warn(`Failed to load lightning hazards for POI detailed map index ${i}:`, err);
        }
      });

      // Diurnal strike trend chart
      const chartCanvas = document.getElementById(`poi-lightning-trend-chart-${i}`) as HTMLCanvasElement;
      if (chartCanvas && manifest) {
        if (!TierAccessService.userHasLayerAccess('lightning')) {
          const parent = chartCanvas.parentElement;
          if (parent) {
            chartCanvas.style.display = 'none';
            parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
              denial: ForecastBinaryService.lastAccessDenial,
              requiredTier: getRequiredTierForLayer('lightning'),
              featureLabel: 'diurnal lightning strike risk trend charts',
              compact: true
            });
          }
          continue;
        }
        fetchLightningTrend(lat, lon, curDateStr, manifest, 'blend', cleanViewStr === '1')
          .then((trendData: any) => {
            if (!chartCanvas) return;
            if ((!trendData.labels || trendData.labels.length === 0) && ForecastBinaryService.lastAccessDenial) {
              const parent = chartCanvas.parentElement;
              if (parent) {
                chartCanvas.style.display = 'none';
                parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
                  denial: ForecastBinaryService.lastAccessDenial,
                  requiredTier: getRequiredTierForLayer('lightning'),
                  featureLabel: 'diurnal lightning strike risk trend charts',
                  compact: true
                });
              }
              return;
            }
            const levelColors = ['rgba(255,255,255,0.05)', '#9664ff', '#ff8000', '#ff0000'];
            const bgColors = trendData.levels.map((lvl: any) => levelColors[lvl] || 'rgba(255,255,255,0.05)');
            const borderColors = trendData.levels.map((lvl: any) => lvl > 0 ? (levelColors[lvl] || '#eab308') : 'transparent');

            const lChart = new Chart(chartCanvas, {
              type: 'bar',
              data: {
                labels: trendData.labels,
                datasets: [{
                  label: 'Strike Risk',
                  data: trendData.levels,
                  backgroundColor: bgColors,
                  borderColor: borderColors,
                  borderWidth: 1,
                  borderRadius: 2
                }]
              },
              options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: { duration: 300 },
                plugins: {
                  legend: { display: false },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const val = context.parsed.y;
                        const idx = val !== null && val !== undefined ? Number(val) : 0;
                        const labels = ['None (<10)', 'Low Risk (10-35)', 'Moderate Risk (35-70)', 'High Risk (70+)'];
                        return ` Strike Risk: ${labels[idx] || 'None'}`;
                      }
                    }
                  }
                },
                scales: {
                  y: {
                    min: 0,
                    max: 3,
                    ticks: {
                      stepSize: 1,
                      color: '#94a3b8',
                      font: { size: 7 },
                      callback: (val: any) => {
                        const labels = ['0', 'Low', 'Mod', 'Hi'];
                        return labels[Number(val)] || '';
                      }
                    },
                    grid: { color: 'rgba(255,255,255,0.05)' }
                  },
                  x: {
                    ticks: {
                      color: '#94a3b8',
                      font: { size: 7 },
                      maxRotation: 0,
                      autoSkip: true,
                      maxTicksLimit: 6
                    },
                    grid: { display: false }
                  }
                }
              }
            });
            ctx.charts.push(lChart);
          })
          .catch((err: any) => {
            console.warn(`Failed to render POI lightning trend chart for day ${i}:`, err);
          });
      }
    }
  }
}
